import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';

import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';

import { useGetPnrDiscoveries } from '~/hooks/apis/flights/usePnrDiscoveries';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { PnrDiscovery } from '~/services/FlightsService';

import isProd from '~/utils/isProd';

import CancelPnrImportDialog from './PnrImport/CancelPnrImportDialog';
import PnrImportDialog from './PnrImport/PnrImportDialog';
import { PNR_IMPORT_BRANDS, canCancelPnrImport } from './PnrImport/pnrImport';

const IMPORTABLE_STATUSES: Array<PnrDiscovery['status']> = ['discovered', 'reservation_created'];

const buildColumns = (onCancelImport: (orderId: string) => void): Array<GridColDef<PnrDiscovery>> => [
  { field: 'pnr_id', headerName: 'PNR', width: 130 },
  { field: 'owner_pcc', headerName: 'PCC', width: 90 },
  { field: 'brand_code', headerName: 'Brand', width: 90 },
  { field: 'customer_name', headerName: 'Customer', width: 170 },
  { field: 'contact_email', headerName: 'Contact email', flex: 1, minWidth: 230 },
  { field: 'contact_phone', headerName: 'Contact phone', width: 160 },
  { field: 'airline_codes', headerName: 'Airlines', width: 110 },
  { field: 'status', headerName: 'Status', width: 170 },
  { field: 'ticketing_deadline', headerName: 'Ticketing deadline', width: 230 },
  {
    field: 'order_id',
    headerName: 'Order',
    width: 260,
    renderCell: ({ row }) =>
      row.order_id && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            component="a"
            href={`/purchases/${row.order_id}`}
            onClick={(event) => event.stopPropagation()}
          >
            View order
          </Button>
          {canCancelPnrImport(row) && (
            <Button
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                onCancelImport(row.order_id);
              }}
            >
              Cancel import
            </Button>
          )}
        </Stack>
      ),
  },
  { field: 'first_seen_at', headerName: 'Discovered', width: 230 },
];

/** A PNR can join a new order while it has no order, is not ticketing-expired and was not booked online. */
const isImportable = (discovery: PnrDiscovery) => !discovery.order_id && IMPORTABLE_STATUSES.includes(discovery.status);

export default function PnrDiscoveriesPage() {
  const { tenant } = useCurrentTenant();
  const [search, setSearch] = useState('');
  const [discoveredOnly, setDiscoveredOnly] = useState(false);
  const query = useDeferredValue(search.trim());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [cancelImportOrderId, setCancelImportOrderId] = useState<string | null>(null);
  const columns = useMemo(() => buildColumns(setCancelImportOrderId), []);

  const discoveries = useGetPnrDiscoveries({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    q: query,
    discoveredOnly,
  });
  const rows = discoveries.data?.result ?? [];
  const selectedDiscoveries = rows.filter((row) => selection.includes(row.id));
  const isBrandSupported = PNR_IMPORT_BRANDS.includes(tenant.brand);
  // PNRs cannot be selected, so cannot be imported, in production.
  const isProduction = isProd();

  const openOrder = useCallback(({ row }: GridRowParams<PnrDiscovery>) => {
    if (row.order_id) {
      window.location.assign(`/purchases/${row.order_id}`);
    }
  }, []);

  const resetPage = () => setPaginationModel((model) => ({ ...model, page: 0 }));

  return (
    <Stack spacing={2}>
      <Typography variant="h5">PNR import</Typography>
      {!isBrandSupported && (
        <Alert severity="info">PNRs can only be imported for Luxury Escapes and LE Business Traveller.</Alert>
      )}
      <TextField
        label="Search customer, contact, PNR, airline or brand"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          resetPage();
        }}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={discoveredOnly}
              onChange={(event) => {
                setDiscoveredOnly(event.target.checked);
                resetPage();
              }}
            />
          }
          label="Show discovered PNRs only"
        />
        <Button
          variant="contained"
          disabled={!selectedDiscoveries.length || !isBrandSupported}
          onClick={() => setIsImportOpen(true)}
        >
          Import selected ({selectedDiscoveries.length})
        </Button>
      </Stack>
      {discoveries.isError && <Alert severity="error">Could not load Flight PNRs.</Alert>}
      <Box sx={{ height: 560 }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={discoveries.isLoading}
          checkboxSelection
          disableRowSelectionOnClick
          isRowSelectable={({ row }) => !isProduction && isImportable(row)}
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          onRowClick={openOrder}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
            setSelection([]);
          }}
          rowCount={discoveries.data?.pagination.total ?? 0}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
      {isImportOpen && (
        <PnrImportDialog
          discoveries={selectedDiscoveries}
          brand={tenant.brand}
          onClose={() => setIsImportOpen(false)}
          onImported={(orderId) => window.location.assign(`/purchases/${orderId}`)}
        />
      )}
      {cancelImportOrderId && (
        <CancelPnrImportDialog orderId={cancelImportOrderId} onClose={() => setCancelImportOrderId(null)} />
      )}
    </Stack>
  );
}
